/**
 * Hook useCrmDealsPaginated - Paginação server-side para listagem de deals
 * Utiliza RPC get_deals_paginated com offset-based pagination
 * Versão: 1.2.0 - Adicionados workspace_id, workspace_name, source, tags
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { DealFilters, DealStatus, DealPriority } from '@/hooks/useCrmDeals';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('CrmDealsPaginated');

/**
 * Campos permitidos para ordenação (whitelist segura)
 * Mapeados para colunas do banco de dados
 */
export type DealSortField = 
  | 'value' 
  | 'probability' 
  | 'created_at' 
  | 'status' 
  | 'phase_sort_order' 
  | 'expected_close_date' 
  | 'actual_close_date'
  | 'assigned_user_name' 
  | 'seller_name';

/**
 * Direção de ordenação
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Interface do deal retornado pela RPC get_deals_paginated
 * Estrutura flat otimizada para tabelas
 */
export interface PaginatedDeal {
  id: string;
  title: string;
  company_name: string | null;
  value: number;
  currency: string;
  priority: DealPriority;
  status: DealStatus;
  probability: number | null;
  expected_close_date: string | null;
  created_at: string;
  actual_close_date: string | null;
  sort_order: number;
  // Contato
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_avatar: string | null;
  // Fase
  phase_id: string;
  phase_name: string | null;
  phase_color: string | null;
  phase_sort_order: number | null;
  phase_is_won: boolean;
  phase_is_lost: boolean;
  // Pipeline
  pipeline_id: string;
  pipeline_name: string | null;
  // Responsável
  assigned_to: string | null;
  assigned_user_name: string | null;
  assigned_user_avatar: string | null;
  // Vendedor
  seller_id: string | null;
  seller_name: string | null;
  seller_avatar: string | null;
  // Paginação
  total_count: number;
  // v1.2.0: Workspace, Origem e Tags
  workspace_id: string | null;
  workspace_name: string | null;
  source: string | null;
  tags: Array<{ tag_id: string; tag_name: string; tag_color: string }>;
  // v1.3.0 (2026-05-16): Origem do cadastro do negócio (manual/import/webhook/etc)
  creation_origin: {
    type: 'manual' | 'import' | 'webhook' | 'scheduling' | 'flow' | 'api';
    source_id?: string | null;
    source_name?: string | null;
    extra?: Record<string, unknown> | null;
  } | null;
}

/**
 * Parâmetros de paginação e ordenação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: DealSortField;
  sortDirection?: SortDirection;
}

/**
 * Resultado da query paginada
 */
interface PaginatedDealsResult {
  deals: PaginatedDeal[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar deals com paginação server-side
 * Utiliza RPC get_deals_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (workspace, pipeline, status, etc.)
 * @param pagination - Parâmetros de paginação (page, pageSize)
 */
export function useCrmDealsPaginated(
  filters: DealFilters | undefined,
  pagination: PaginationParams
): PaginatedDealsResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['crm-deals-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando deals paginados:', { filters, pagination });

      // Chama RPC com todos os parâmetros (v1.0.3 - inclui ordenação)
      const { data, error } = await supabase.rpc('get_deals_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        // Filtros básicos
        p_workspace_id: filters?.workspace_id || null,
        p_pipeline_id: filters?.pipeline_id || null,
        p_phase_id: filters?.phase_id || null,
        p_status: filters?.status || null,
        p_priority: filters?.priority || null,
        p_search: filters?.search || null,
        // Filtros avançados
        p_assigned_to: filters?.assigned_to || null,
        p_seller_id: filters?.seller_id || null,
        p_source: filters?.source || null,
        // v1.7.0 (2026-07-11): Multi-seleção de origens (get_deals_paginated v1.11.0)
        p_source_ids:
          filters?.source_ids && filters.source_ids.length > 0
            ? filters.source_ids
            : null,
        p_probability_min: filters?.probability_min ?? null,
        p_probability_max: filters?.probability_max ?? null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        // Parâmetros de ordenação (v1.0.3)
      p_sort_field: pagination.sortField || 'created_at',
      p_sort_direction: pagination.sortDirection || 'desc',
      // v1.1.0: Filtros de data avançados
      p_created_from: filters?.created_from || null,
      p_created_to: filters?.created_to || null,
      p_won_date_from: filters?.won_date_from || null,
      p_won_date_to: filters?.won_date_to || null,
      p_lost_date_from: filters?.lost_date_from || null,
      p_lost_date_to: filters?.lost_date_to || null,
      p_phase_updated_from: filters?.phase_updated_from || null,
      p_phase_updated_to: filters?.phase_updated_to || null,
      // v1.3.0 (2026-04-23): Tags e Data Previsão
      // O UUID sentinela é interpretado exclusivamente pela RPC como "sem tags".
      p_tag_ids: filters?.tags_is_empty
        ? ['00000000-0000-0000-0000-000000000000']
        : filters?.tag_ids && filters.tag_ids.length > 0 ? filters.tag_ids : null,
      p_expected_close_from: filters?.expected_close_from || null,
      p_expected_close_to: filters?.expected_close_to || null,
      // v1.4.0 (2026-05-16): Filtros de Origem do Cadastro
      p_creation_origin_types:
        filters?.creation_origin_types && filters.creation_origin_types.length > 0
          ? filters.creation_origin_types
          : null,
      p_creation_origin_source_id: filters?.creation_origin_source_id || null,
      // v1.5.0 (2026-05-31): Timezone do navegador para alinhar filtros de data com KPIs
      p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      // v1.6.0 (2026-07-11): Filtro de motivos de perda (multi-seleção)
      p_loss_reason_ids:
        filters?.loss_reason_ids && filters.loss_reason_ids.length > 0
          ? filters.loss_reason_ids
          : null,
      // v1.8.0 (2026-09-07): grupos "sem valor" da listagem agrupada
      p_seller_is_null: filters?.seller_is_null === true,
      p_workspace_is_null: filters?.workspace_is_null === true,
      p_source_is_null: filters?.source_is_null === true,
      // v1.9.0 (2026-09-17): recortes de auditoria dos indicadores
      // (get_deals_paginated v1.14.0). Nulos = comportamento anterior.
      p_product_ids: filters?.product_ids?.length ? filters.product_ids : null,
      p_item_unit_types: filters?.item_unit_types?.length ? filters.item_unit_types : null,
      p_recurrence_intervals: filters?.recurrence_intervals?.length
        ? filters.recurrence_intervals
        : null,
      p_term_types: filters?.term_types?.length ? filters.term_types : null,
      p_exclude_unit_types: filters?.exclude_unit_types?.length
        ? filters.exclude_unit_types
        : null,
      p_customer_ids: filters?.customer_ids?.length ? filters.customer_ids : null,
      // v1.10.0 (2026-10-05): auditoria do KPI de descontos por existência de item descontado
      p_has_discount: filters?.has_discount ?? null,
    } as any);



      if (error) {
        logger.error('Erro ao buscar deals paginados:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawDeals = (data || []) as unknown as PaginatedDeal[];
      const totalCount = rawDeals[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawDeals.length} deals, total: ${totalCount}`);

      return {
        deals: rawDeals,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    deals: query.data?.deals || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['crm-deals-paginated', currentTenant?.id] 
      });
    },
  };
}
